import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDraft } from "@/components/import/DraftContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const show = (v) => (v == null || v === "" ? "—" : Array.isArray(v) ? v.join(", ") || "—" : String(v));

export function DiffDialog({ item, onClose }) {
  const { id } = useDraft();
  const { data, isError } = useQuery({ queryKey: ["imports", id, "diff", item?.item_id], enabled: Boolean(item),
    queryFn: () => api.get(`/imports/${id}/items/${item.item_id}/diff`).then((r) => r.data) });
  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-[#2A3447] bg-[#121620] text-slate-200" data-testid="import-diff-dialog">
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Changes to {data?.name || "existing card"}</DialogTitle><DialogDescription className="sr-only">Differences between the import and your existing card</DialogDescription></DialogHeader>
        {isError && <p className="text-sm text-red-300">The existing card could not be loaded.</p>}
        {data && (data.changes.length === 0 && !data.images.some((i) => i.replaces)
          ? <p className="text-sm text-slate-400" data-testid="import-diff-none">No differences in the card data.</p>
          : (
            <div className="space-y-2 text-sm" data-testid="import-diff-list">
              {data.changes.map((c) => (
                <div key={c.field} className="rounded border border-[#2A3447] p-3">
                  <p className="font-mono text-xs text-amber-300">{c.field}</p>
                  <p className="mt-1 whitespace-pre-line break-words text-red-300/80 line-through">{show(c.before)}</p>
                  <p className="whitespace-pre-line break-words text-emerald-300">{show(c.after)}</p>
                </div>
              ))}
              {data.images.filter((i) => i.replaces).map((i) => <p key={i.face} className="text-amber-200">Face {i.face + 1} image will be replaced (the old one stays in history).</p>)}
            </div>
          ))}
        <p className="text-xs text-slate-500">Updating keeps the card's permanent ID, decks, set and privacy. Version v{data?.version} is saved in its history.</p>
      </DialogContent>
    </Dialog>
  );
}

const editable = (item) => {
  const { faces = [], ...rest } = Object.fromEntries(Object.entries(item.card || {}).filter(([, v]) => v != null));
  const flat = faces.length === 1 ? Object.fromEntries(Object.entries(faces[0]).filter(([, v]) => v != null && v !== "")) : { faces };
  return { import_id: item.import_id, ...rest, ...flat, related: item.related, ...(item.image_refs?.some(Boolean) ? { images: item.image_refs } : {}) };
};

export function ItemEditDialog({ item, onClose }) {
  const { id, refresh } = useDraft();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      return toast.error(`Not valid JSON: ${e.message}`);
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/imports/${id}/items/${item.item_id}`, { card: parsed });
      refresh();
      data.errors.length ? toast.warning(`${data.errors.length} problem(s) remain`) : toast.success("Card data saved");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => { if (!o) onClose(); else setText(JSON.stringify(editable(item), null, 2)); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-[#2A3447] bg-[#121620] text-slate-200" data-testid="import-edit-dialog"
        onOpenAutoFocus={() => setText(JSON.stringify(editable(item), null, 2))}>
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Fix card data</DialogTitle><DialogDescription className="sr-only">Edit this card in import format</DialogDescription></DialogHeader>
        <p className="text-sm text-slate-400">Edit the fields in import format. Nothing is rewritten for you; the card is checked again when you save.</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={18} className="field font-mono text-xs" data-testid="import-edit-textarea" />
        <div className="flex justify-end gap-3">
          <button className="btn-ghost" onClick={onClose} data-testid="import-edit-cancel">Cancel</button>
          <button className="btn-gold" onClick={save} disabled={busy} data-testid="import-edit-save">Save and re-check</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
